
import { useEffect, useMemo, } from "react";
import { TypedTable } from "@/components/ui/table/TypedTable";
import { createColumnHelper, tableFeatures, type RowSelectionState } from "@tanstack/react-table";
import { useOrdenes, type InfoOF } from "@/hooks/Api/OrdenesFabricacion";
import { CommonFeatures } from "@/hooks/useTanstackTable";
import { getSelectionCell } from "@/components/ui/table/datatable";

interface TablaOrdenesGestorProps {
	readonly filtroMaquina: string | undefined;
	readonly ofFocused: InfoOF | undefined;
	readonly setOfFocused: (of: InfoOF | undefined) => void;
	readonly selectedOFs: RowSelectionState;
	readonly setSelectedOFs: Setter<RowSelectionState>;
}
export function TablaOrdenesGestor(props: TablaOrdenesGestorProps) {
	const { setSelectedOFs, setOfFocused } = props;
	const onClickRow = (event: React.MouseEvent, data: InfoOF) => {
		const target = event.target as HTMLElement;
		if (event.ctrlKey) { // Toggle checkbox on ctrl+click
			const checkbox = target.closest("tr")?.querySelector("input[type='checkbox']") as HTMLInputElement | null;
			if (!checkbox) throw new Error("la fila no tiene checkbox");

			checkbox.checked = !checkbox.checked;
			return;
		}
		if (target.closest(".noClick")) return; //Si he hecho click en el checkbox, no entro a detalles

		setOfFocused(data);
	};

	const { columns, features } = useMemo(() => Columns(), []);

	const { data, error, isPending } = useOrdenes({
		idMaquina: props.filtroMaquina
	});


	// Reset selected rows when data changes
	useEffect(() => {
		setSelectedOFs({});
	}, [setSelectedOFs, data]);

	// Reset selected rows when data changes
	useEffect(() => {
		if (props.ofFocused == null) return;
		const of = data?.find(item => item.idOF === props.ofFocused?.idOF);
		if (Object.is(of, props.ofFocused)) return;
		setOfFocused(of);
	}, [setOfFocused, props.ofFocused, data]);

	const dataExtendida = useMemo(() => data?.map(extendInfoOF), [data]);



	return (
		<TypedTable
			className="dataTable display clicable"
			data={dataExtendida ?? []}
			columns={columns}
			features={features}
			onRowClick={onClickRow}
			rowClassName={row => props.ofFocused?.idOF === row.idOF ? "clicked" : ""}

			pagination
			globalSearch

			getRowId={row => row.idOF.toString()}
			selection={props.selectedOFs}
			setSelection={setSelectedOFs}

			noResultsMessage="No se encontraron pedidos"
			errorMessage={error?.message ?? "Error al cargar las ordenes de fabricacion"}
			isLoading={isPending}
			isError={error != null}
		/>
	);
}

function Columns() {
	const columnHelper = createColumnHelper<typeof features, InfoOFExtendida>();
	const features = tableFeatures(CommonFeatures);

	const columns = columnHelper.columns([
		columnHelper.display({
			id: "checkbox",
			// header: () => <input type="checkbox" className="checkboxRow" style={{ height: 20 }} />,
			meta: { className: "dt-center noClick" },
			cell: (info) => getSelectionCell(info.row),
		}),

		columnHelper.accessor("idOF", { header: "Número de OF" }),
		columnHelper.accessor("idConjunto", { header: "Conjunto" }),
		columnHelper.accessor("descripcion", { header: "Descripción" }),
		columnHelper.accessor("situacion", {
			header: "Situación",
			cell: (info) => {
				const of = info.row.original;

				if (of.situacion === 2)
					return `Cerrada (${formatter.format(of.percent)}%)`;
				else if (of.situacion === 0)
					return "Sin iniciar";
				else
					return `En curso (${formatter.format(of.percent)}%)`;
			},
			sortFn: (rowA, rowB) => {
				if (rowA.original.situacion < rowB.original.situacion) return -1;
				if (rowA.original.situacion > rowB.original.situacion) return 1;
				if (rowA.original.percent < rowB.original.percent) return -1;
				if (rowA.original.percent > rowB.original.percent) return 1;
				return 0;
			},
		}),
		columnHelper.accessor("cantLanzada", { header: "Cantidad" }),
		columnHelper.accessor((row) => row.fases.length, { id: "cantFases", header: "Cantidad Fases" }),
		// { title: "Fecha inicio previsto", render: (ts) => displayDate(ts.horasCalculadas) },
	]);
	return { columns, features };
}


type InfoOFExtendida = InfoOF & { situacion: 0 | 1 | 2; percent: number; };
function extendInfoOF(orden: InfoOF): InfoOFExtendida {
	let horasCalculadas = 0;
	let horasImputadas = 0;
	for (const fase of orden.fases) {
		const tiempoEjecucion = (fase.tiempoEjecucionMano ?? 0) + (fase.tiempoEjecucionMaq ?? 0);
		horasCalculadas += (fase.cantLanzada || orden.cantLanzada) * tiempoEjecucion;
		horasImputadas += fase.cantAcabada * tiempoEjecucion;
	}

	const porcentaje = (horasImputadas / horasCalculadas) * 100;

	const cerrada = orden.fases.every(fase => fase.timestampFin != null);
	const sinAbrir = orden.fases.every(fase => fase.timestampIni == null);

	return {
		...orden,
		// eslint-disable-next-line no-nested-ternary -- Simple enough
		situacion: cerrada ? 2 : sinAbrir ? 0 : 1,
		percent: porcentaje,
	};
}
//Yo encantado usaria style:percent, pero se niega a formatear en "narrow"
const formatter = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 1 });